import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { CandidateForm } from './CandidateForm';
import { InterviewProcess } from './InterviewProcess';
import { useInterviewStore } from '../store';
import { generateInterviewQuestion } from '../services/openai';
import toast from 'react-hot-toast';
import { BrainCircuitIcon, ArrowLeftIcon, Loader2Icon } from 'lucide-react';

export function CandidateInterview() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [interview, setInterview] = useState<any>(null);
  const setJobDescription = useInterviewStore((state) => state.setJobDescription);
  const setCurrentInterview = useInterviewStore((state) => state.setCurrentInterview);
  const updateConfig = useInterviewStore((state) => state.updateConfig);

  useEffect(() => {
    if (!token) {
      setError('Interview token is required');
      return;
    }
    loadInterview();
  }, [token]);

  const loadInterview = async () => {
    try {
      setLoading(true);
      setError(null);

      // First verify the interview exists and is accessible
      const { data: interviews, error: fetchError } = await supabase
        .from('interviews')
        .select(`
          *,
          interview_responses (
            id,
            candidate_name,
            candidate_email,
            status,
            responses,
            video_urls,
            score,
            feedback
          )
        `)
        .eq('share_token', token)
        .eq('status', 'active');

      if (fetchError) {
        console.error('Error fetching interview:', fetchError);
        throw new Error(t('interview.candidate.notFound'));
      }

      if (!interviews || interviews.length === 0) {
        throw new Error(t('interview.candidate.notFound'));
      }

      const interview = interviews[0];

      // Verify interview has questions configured
      if (!interview.config?.questions?.length) {
        throw new Error(t('interview.candidate.noQuestions'));
      }

      setInterview(interview);

      // Set interview configuration
      setJobDescription(interview.config.jobDescription);
      updateConfig(interview.config);
    } catch (error) {
      console.error('Error loading interview:', error);
      setError(error instanceof Error ? error.message : t('interview.candidate.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCandidateFormComplete = async (newResponseId: string) => {
    try {
      setResponseId(newResponseId);
      
      // Get current config and job description
      const config = useInterviewStore.getState().interviewConfig;
      const jobDescription = useInterviewStore.getState().jobDescription;

      if (!jobDescription) {
        throw new Error('Job description not found');
      }

      // Generate Sarah's greeting
      const greeting = await generateInterviewQuestion(
        jobDescription,
        {
          ...config,
          isInitialGreeting: true
        },
        [],
        []
      );

      // Set current interview with greeting
      setCurrentInterview({
        id: newResponseId,
        jobTitle: interview.job_title,
        candidateName: '',
        date: new Date().toISOString(),
        status: 'in-progress',
        responses: [],
        currentQuestion: greeting,
        hasIntroduced: false,
        questions: interview.config.questions || []
      });

    } catch (error) {
      console.error('Error starting interview:', error);
      toast.error(t('interview.candidate.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2Icon className="h-8 w-8 animate-spin text-indigo-600" />
          <p className="text-gray-600">{t('interview.candidate.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-lg w-full text-center">
          <div className="flex justify-center mb-6">
            <div className="p-3 bg-red-100 rounded-full">
              <BrainCircuitIcon className="h-8 w-8 text-red-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            {t('interview.candidate.error')}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {t('interview.candidate.returnHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2">
            <BrainCircuitIcon className="h-8 w-8 text-indigo-600" />
            <h1 className="text-2xl font-bold text-gray-900">
              {t('app.title')}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {responseId ? (
          <InterviewProcess />
        ) : (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white shadow rounded-lg p-6 space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {t('interview.candidate.welcome')}
                </h2>
                <p className="text-gray-600">
                  {t('interview.candidate.instructions')}
                </p>
              </div>

              <CandidateForm
                interviewId={interview.id}
                onComplete={handleCandidateFormComplete}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}